import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatDurationMs } from '../vibeCodingUtils';
import {
  RunningSpinner,
  TodoBox,
  getBuildStepsState,
  getLiveBadgeStyle,
  getStepRowStyle,
  getTodoIcon,
} from '../utils/progressUtils';

const BuildSteps = ({ milestones, phase, isRunning }) => {
  const [showAllSteps, setShowAllSteps] = useState(false);

  const {
    steps,
    completedSteps,
    progressPercent,
    hasFailed,
    runningIndex,
    isRework,
    reworkStep,
    statusColor,
    statusText,
    activeIndex,
    allDone,
  } = getBuildStepsState(milestones, isRunning);

  const phaseDetail = phase && phase.detail ? phase.detail : null;

  const renderStepRow = (step, idx, isActive) => {
    const isDone = step.status === 'completed';
    const isFailed = step.status === 'failed' || step.status === 'stopped';
    const isLive = isActive && step.status === 'running';
    const elapsed = isDone && step.elapsedMs ? formatDurationMs(step.elapsedMs) : null;
    return (
      <li
        key={step.id || idx}
        aria-current={isActive ? 'step' : undefined}
        className="d-flex align-items-center px-2 py-1 rounded"
        style={getStepRowStyle({ isLive, isDone, isFailed, isRework })}
      >
        <span className="mr-2 d-inline-flex align-items-center justify-content-center">
          {isLive ? <RunningSpinner /> : getTodoIcon(step.status)}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={step.title}
        >
          {step.title}
        </span>
        {isLive && (
          <span className="badge ml-2" style={getLiveBadgeStyle(isRework)}>
            {isRework ? 'Fixing…' : 'In progress…'}
          </span>
        )}
        {!isLive && step.status === 'pending' && isActive && (
          <span
            className="badge ml-2"
            style={{ fontSize: '10px', background: '#f1f5f9', color: '#64748b' }}
          >
            Up next
          </span>
        )}
        {isFailed && (
          <span
            className="badge ml-2"
            style={{ fontSize: '10px', background: '#fee2e2', color: '#b91c1c' }}
          >
            Needs attention
          </span>
        )}
        {elapsed && (
          <span className="ml-2" style={{ fontSize: '11px', color: '#94a3b8' }}>
            {elapsed}
          </span>
        )}
      </li>
    );
  };

  return (
    <div
      className="vibe-build-steps flex-shrink-0"
      style={{ borderTop: '1px solid #eef2f7', paddingTop: '10px', marginTop: '6px' }}
    >
      <div
        className="d-flex align-items-center justify-content-between mb-2"
        style={{ paddingLeft: '4px' }}
      >
        <div className="d-flex align-items-center">
          <span
            className="mr-2 d-inline-block rounded-circle"
            style={{
              width: '8px',
              height: '8px',
              flexShrink: 0,
              backgroundColor: statusColor,
              boxShadow: isRunning && !hasFailed ? '0 0 8px #3b82f6' : 'none',
              animation: isRunning && !hasFailed ? 'vibePulse 1.2s infinite' : 'none',
            }}
            aria-hidden="true"
          />
          <span className="font-weight-bold text-dark" style={{ fontSize: '14px' }}>
            {statusText}
          </span>
          {steps.length > 0 && (
            <span className="badge badge-light text-muted border ml-2" style={{ fontSize: '11px' }}>
              {completedSteps} of {steps.length} steps
            </span>
          )}
        </div>
        <div className="d-flex align-items-center">
          {steps.length > 1 && (
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setShowAllSteps(!showAllSteps)}
              style={{
                fontSize: '12px',
                color: '#2563eb',
                textDecoration: 'none',
                fontWeight: 500,
              }}
              aria-expanded={showAllSteps}
              aria-label={showAllSteps ? 'Hide all steps' : 'Show all steps'}
            >
              {showAllSteps ? 'Hide steps' : 'Show all steps'}
            </button>
          )}
        </div>
      </div>

      {steps.length > 0 && (
        <div
          className="progress mb-2"
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label="Build progress"
          style={{ height: '4px', background: '#f1f5f9', borderRadius: '4px' }}
        >
          <div
            className="progress-bar"
            style={{
              width: `${progressPercent}%`,
              backgroundColor: statusColor,
              transition: 'width 0.4s ease',
              borderRadius: '4px',
            }}
          />
        </div>
      )}

      {steps.length > 0 && (
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden',
            background: '#ffffff',
          }}
        >
          <ol className="list-unstyled mb-0 p-1" aria-label="Build steps">
            {allDone ? (
              <li
                className="d-flex align-items-center px-2 py-1"
                style={{ fontSize: '13px', color: '#15803d', fontWeight: 600 }}
              >
                <span className="mr-2 d-inline-flex align-items-center justify-content-center">
                  <TodoBox checked />
                </span>
                All steps completed
              </li>
            ) : (
              <>
                {steps.map((step, idx) =>
                  showAllSteps || idx === activeIndex
                    ? renderStepRow(step, idx, idx === activeIndex)
                    : null,
                )}
              </>
            )}
          </ol>
          {isRework && reworkStep && (
            <div
              className="mx-2 mb-2 px-2 py-1 rounded"
              role="status"
              style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                fontSize: '12px',
                color: '#92400e',
              }}
            >
              Step {runningIndex + 1} “{reworkStep.title}” is running again to fix an issue.
              Completed progress is preserved.
            </div>
          )}
        </div>
      )}

      {phaseDetail && (
        <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
          {phaseDetail}
        </div>
      )}
    </div>
  );
};

BuildSteps.propTypes = {
  milestones: PropTypes.array,
  phase: PropTypes.object,
  isRunning: PropTypes.bool,
};

BuildSteps.defaultProps = {
  milestones: [],
  phase: null,
  isRunning: false,
};

export default BuildSteps;
